/**
 * Script Vault V2 – Reuses Hall of Fame components with showcase data.
 *
 * This page is visually identical to /best (HallOfFame/Best) but shows
 * showcase=1 movies (professionally produced screenplays) instead of
 * listing/criteria movies.
 */
import React, { useState } from 'react';
import HallOfFameTable from '../HallOfFame/HallOfFameTable';
import '../HallOfFame/styling.css';

// Showcased TV series reports (SeriesShowcase::examples): a series is read as a whole, so it gets its own cards
// above the single-script table rather than a row in it.
function SeriesExamples({ series }) {
    if (!series || series.length === 0) return null;
    return (
        <div className="bg-white border border-top-0 px-3 px-lg-4 py-3">
            <h3 className="h5 fw-bold mb-1" style={{ color: '#14536e' }}>TV series, analyzed as a whole season</h3>
            <p className="mb-3 text-muted" style={{ maxWidth: 720 }}>
                Every episode gets its own full analysis, and the season gets a report of its own: what each reader
                concluded, a map of the storylines across episodes, characters, and the Layered Read from season down to scene.
            </p>
            <div className="row g-3">
                {series.map(s => (
                    <div className="col-md-6 col-lg-4" key={s.uuid}>
                        <a href={s.url} className="d-block h-100 border rounded-2 p-3 text-decoration-none" style={{ color: '#1f2933' }}>
                            <div className="fw-bold" style={{ color: '#14536e' }}>{s.name}</div>
                            <div className="small text-muted mb-2">{s.episodes} episodes</div>
                            {s.premise && <div className="small mb-2" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s.premise}</div>}
                            {s.readers.length > 0 && (
                                <div className="small mb-2">{s.readers.map(r => `${r.count} ${r.verdict}`).join(', ')}</div>
                            )}
                            <span className="fw-semibold small" style={{ color: '#14536e' }}>Open the series report &rarr;</span>
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function Index({ scripts, series = [], auth }) {
    const [filteredCount, setFilteredCount] = useState(scripts.length);

    // Same teal gradient as the Hall of Fame header
    const seamlessBgStyle = {
        background: 'linear-gradient(135deg, #083A54 0%, #0A6A64 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
    };

    const [showInfo, setShowInfo] = useState(false);

    return (
        <>
            <div className="container-xxl">
                <div className="row justify-content-center pt-4">
                    <div className="col-lg-12">

                        {/* HEADER — same structure as Hall of Fame but with Vault branding */}
                        <div className="shadow-sm rounded-top-2 text-white overflow-hidden mb-0" style={seamlessBgStyle}>
                            <div className="p-3">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 px-lg-2">

                                    {/* Left: Title + Count */}
                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                        <h2 className="h4 mb-0 fw-bold">
                                            <i className="fa fa-film text-warning me-2"></i>
                                            The Script Vault
                                        </h2>
                                        <span
                                            className="badge"
                                            style={{
                                                background: filteredCount < scripts.length
                                                    ? 'rgba(255, 193, 7, 0.3)'
                                                    : 'rgba(255, 255, 255, 0.25)',
                                                color: 'white',
                                                padding: '6px 16px',
                                                borderRadius: '20px',
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                border: filteredCount < scripts.length
                                                    ? '1px solid rgba(255, 193, 7, 0.5)'
                                                    : '1px solid rgba(255, 255, 255, 0.4)'
                                            }}
                                        >
                                            {filteredCount === scripts.length
                                                ? `${scripts.length} ${scripts.length === 1 ? 'Script' : 'Scripts'}`
                                                : `${filteredCount} of ${scripts.length} Scripts`
                                            }
                                        </span>
                                    </div>

                                    {/* Right: Info toggle */}
                                    <button
                                        onClick={() => setShowInfo(!showInfo)}
                                        className="btn btn-sm"
                                        style={{
                                            fontSize: '0.85rem',
                                            background: 'rgba(255, 255, 255, 0.15)',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            color: 'white',
                                            fontWeight: 600,
                                            padding: '8px 16px',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                        }}
                                    >
                                        <i className={`fa ${showInfo ? 'fa-chevron-up' : 'fa-info-circle'} me-2`}></i>
                                        {showInfo ? 'Close' : 'About The Vault'}
                                    </button>
                                </div>

                                <p
                                    className="mb-0 small mt-2 px-lg-2"
                                    style={{
                                        maxWidth: '800px',
                                        lineHeight: '1.5',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    Professional produced screenplays analyzed by Script Reader. These serve as the baseline for scoring all submitted scripts.
                                </p>

                                {/* Collapsible info drawer */}
                                {showInfo && (
                                    <div className="mt-3 pt-3 border-top border-secondary border-opacity-50 animate-fade-in px-lg-2">
                                        <div className="row g-4">
                                            <div className="col-md-5 border-end border-secondary border-opacity-50">
                                                <h6 className="fw-bold text-uppercase small text-white-50 mb-2">Percentile Key</h6>
                                                <div className="d-flex flex-column gap-2 small">
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge bg-success bg-gradient border border-success me-2" style={{ width: '60px' }}>90%+</span>
                                                        <span><strong>Top Tier.</strong> Exceptional execution.</span>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge bg-primary bg-gradient border border-primary me-2" style={{ width: '60px' }}>70-89%</span>
                                                        <span><strong>Strong.</strong> Above average professional standard.</span>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge bg-light text-dark border me-2" style={{ width: '60px' }}>50-69%</span>
                                                        <span className="opacity-75"><strong>Solid.</strong> Competitive but needs polish.</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-7">
                                                <h6 className="fw-bold text-uppercase small text-white-50 mb-2">How It Works</h6>
                                                <div className="d-flex gap-3 small opacity-75">
                                                    <i className="fa fa-calculator fa-2x mt-1"></i>
                                                    <div>
                                                        <p className="mb-2">
                                                            These are the <strong>professionally produced screenplays</strong> that form the baseline for the Hall of Fame percentile scoring system.
                                                        </p>
                                                        <p className="mb-0 fst-italic">
                                                            When a script on the Hall of Fame shows "46th percentile," it means it scored better than 46% of the scripts in this Vault.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <SeriesExamples series={series} />

                        {/* Table — reuses HallOfFameTable which includes FilterBar, ScriptRow, etc. */}
                        <div className="pb-5 mb-4">
                            <HallOfFameTable scripts={scripts} auth={auth} onFilteredCountChange={setFilteredCount} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
